import { CustomWindow } from "@utils/models/customwindow";
import { useGeneral } from "@utils/general";

// Ensure CustomWindow is available as a value
declare let window: CustomWindow;

const { focusTrap } = useGeneral();

export const useMobileMenuService = () => {
  if (!window.SGI) {
    window.SGI = {
      handleKeyboardNavigation: null,
    };
  }

  const duration = 200;
  const mobileToggleButton = document.querySelector(".menu__toggle-button") as HTMLElement;
  const menuList = document.querySelector(".menu__list") as HTMLElement;
  const mobileToggleButtonOpenClass = "menu__toggle-button--open";
  const menuListOpenClass = "menu__list--open";
  const menuListNavigatedAwayClass = "menu__list--navigated-away";
  const firstLevelToggleClass = "menu__first-level-toggle";
  const subMenuOpenClass = "sub-menu--open";
  const subMenuNavigatedAwayClass = "sub-menu--navigated-away";
  const subMenuToggleButtonClass = "sub-menu__sub-menu-toggle";
  const subMenuFirstLevelToggleClass = "sub-menu__first-level-toggle";

  let parentName: string;
  let currentlyOpenMenuSection: HTMLElement | null = null;

  const toggleMobileMenu = () => {
    // Close menu
    if (menuList?.classList.contains(menuListOpenClass)) {
      document.body.classList.remove("no-scroll");
      document.body.classList.remove("menu-open");
      mobileToggleButton.classList.remove(mobileToggleButtonOpenClass);
      mobileToggleButton.setAttribute("aria-expanded", "false");
      const openMenuLabel = mobileToggleButton.dataset.openlabel || "";
      const spanElement = mobileToggleButton.querySelector("span");
      
      if (spanElement) {
        spanElement.textContent = openMenuLabel;
      }
      mobileToggleButton.focus();
      menuList?.classList.remove(menuListOpenClass);
      
      if (window.SGI && window.SGI.handleKeyboardNavigation) {
        document.removeEventListener('keydown', window.SGI.handleKeyboardNavigation);
      }
      setTimeout(() => { menuList.style.display = "none"; }, duration);
    } 
    // Open menu
    else {
      document.body.classList.add("no-scroll");
      document.body.classList.add("menu-open");
      mobileToggleButton.setAttribute("aria-expanded", "true");
      const closeMenuLabel = mobileToggleButton.dataset.closelabel || "";
      const spanElement = mobileToggleButton.querySelector("span");
      
      if (spanElement) {
        spanElement.textContent = closeMenuLabel;
      }
      
      mobileToggleButton.classList.add(mobileToggleButtonOpenClass);
      
      if (menuList) {
        menuList.style.display = "block";
        setTimeout(() => { menuList.classList.add(menuListOpenClass); }, 1);
        currentlyOpenMenuSection = menuList;

        if (window.SGI && window.SGI.handleKeyboardNavigation) {
          document.removeEventListener('keydown', window.SGI.handleKeyboardNavigation);
          document.addEventListener('keydown', window.SGI.handleKeyboardNavigation);
        }
      }

      openToCurrentPage();
    }
  };

  const openSubMenuMobile = (button: HTMLElement, e: Event, isFirstLevelLink: boolean = false) => {
    stopEvent(e);
    if (window.SGI && window.SGI.handleKeyboardNavigation) {
      document.removeEventListener('keydown', window.SGI.handleKeyboardNavigation);
    }

    const subMenu = getSubMenu(button, isFirstLevelLink);
    subMenu.style.display = "block";

    if (isFirstLevelLink || isFirstLevelLink && button.classList.contains(subMenuToggleButtonClass)) {
      // First level link clicked, just set navigated away
      setTimeout(() => {
        document.querySelector(`.${menuListOpenClass}`)?.classList.add(menuListNavigatedAwayClass);
        subMenu.classList.add(subMenuOpenClass);
      }, 1);
    } else {
      // Sub level clicked, set the last found open submenu to navigated away
      const openSubMenus = document.querySelectorAll(`.${subMenuOpenClass}`);
      setTimeout(() => {
        openSubMenus[openSubMenus.length - 1].classList.add(subMenuNavigatedAwayClass);
        subMenu.classList.add(subMenuOpenClass);
      }, 1);
    }

    // focus the first focusable element in the current menu
    const firstFocusableElement = subMenu.querySelector("button,a") as HTMLElement;
    firstFocusableElement.focus();

    currentlyOpenMenuSection = subMenu;

    if (window.SGI && window.SGI.handleKeyboardNavigation) {
      document.removeEventListener('keydown', window.SGI.handleKeyboardNavigation);
      document.addEventListener('keydown', window.SGI.handleKeyboardNavigation);
    }

    // Set the link name for the go-back-link
    parentName = subMenu.dataset.parentname || getButtonText(button, isFirstLevelLink) as string;
    const parentLink = subMenu.querySelector(".sub-menu__item--back-link button span:last-child") as HTMLElement;
    parentLink.innerText = parentName;
  };

  const closeSubMenuMobile = (button: HTMLElement, e: Event | null = null) => {
    if (window.SGI && window.SGI.handleKeyboardNavigation) {
      document.removeEventListener('keydown', window.SGI.handleKeyboardNavigation);
    }

    if (e) {
      stopEvent(e);
    }

    button.closest(`.${subMenuOpenClass}`)?.classList.remove(subMenuOpenClass);
    const navigatedSubmenu = button.closest(`.${subMenuNavigatedAwayClass}`);

    let tempOldOpenMenuSection: HTMLElement | null = null;
    if (currentlyOpenMenuSection) {
      currentlyOpenMenuSection.classList.remove(subMenuOpenClass);
      tempOldOpenMenuSection = currentlyOpenMenuSection;
    }

    if (navigatedSubmenu) {
      navigatedSubmenu.classList.remove(subMenuNavigatedAwayClass);
      currentlyOpenMenuSection = navigatedSubmenu as HTMLElement;
    } else {
      // If navigatedSubmenu is null the parent is the top container page
      button.closest(`.${menuListNavigatedAwayClass}`)?.classList.remove(menuListNavigatedAwayClass);
      currentlyOpenMenuSection = menuList;
    }

    // focus the first focusable element in the current menu
    const firstFocusableElement = currentlyOpenMenuSection.querySelector("button,a") as HTMLElement;
    firstFocusableElement.focus();

    if (tempOldOpenMenuSection) {
      setTimeout(() => {
        if (tempOldOpenMenuSection) {
          tempOldOpenMenuSection.style.display = "none";
          tempOldOpenMenuSection = null;
        }
      }, duration);
    }

    if (window.SGI && window.SGI.handleKeyboardNavigation) {
      document.removeEventListener('keydown', window.SGI.handleKeyboardNavigation);
      document.addEventListener('keydown', window.SGI.handleKeyboardNavigation);
    }
  };

  const getSubMenu = (button: HTMLElement, isFirstLevelLink: boolean) => {
    if (isFirstLevelLink) {
      if (button.classList.contains(subMenuFirstLevelToggleClass)) {
        // First level chevron was clicked
        return button.parentElement?.nextElementSibling as HTMLElement;
      }
      if (button.classList.contains(firstLevelToggleClass)) { // if (button.classList.contains(menuLinkClass)) {
        // First level link was clicked
        return button.nextElementSibling as HTMLElement;
      }
    }
    // Sub menu chevron was clicked
    return button.parentElement?.nextElementSibling as HTMLElement;
  };

  const getButtonText = (button: HTMLElement, isFirstLevelLink: boolean) => {
    if (isFirstLevelLink) {
      if (button.classList.contains(subMenuFirstLevelToggleClass)) {
        // First level chevron was clicked
        return button.parentElement?.querySelector("span")?.textContent;
      }
      if (button.classList.contains(firstLevelToggleClass)) {
        // First level toggle button was clicked
        return button.querySelector("span.menu-button-name")?.textContent?.trim();
      }
    }

    return button.parentElement?.querySelector("span")?.textContent;
  };

  const openToCurrentPage = () => {
    const openSubMenus = document.querySelectorAll(`.${subMenuOpenClass}`);

    // If there are no open submenus, we're at the root level
    if (!openSubMenus.length) {
      return;
    }

    // Set root level to navigated away
    menuList?.classList.add(menuListNavigatedAwayClass);

    // set all but the last submenu to navigated away
    for (let i = 0; i < openSubMenus.length - 1; i++) {
      const currentSubMenu = openSubMenus[i];
      currentSubMenu.classList.add(subMenuNavigatedAwayClass);
    }

    // Set the link names for the go-back-links
    openSubMenus.forEach(submenu => {
      let linkText = "";
      const currentSubMenu = submenu as HTMLElement;

      if (currentSubMenu.dataset.parentname) {
        linkText = currentSubMenu.dataset.parentname as string;
      }

      const parentLink = currentSubMenu.querySelector(".sub-menu__item--back-link button span:last-child") as HTMLElement;

      if (parentLink != null) {
        parentLink.innerText = linkText;
      }

      currentSubMenu.style.display = "block";
    });

    // Last submenu (in array) is the currently open submenu
    currentlyOpenMenuSection = openSubMenus[openSubMenus.length - 1] as HTMLElement;

    // focus toggle button
    mobileToggleButton.focus();

    if (window.SGI && window.SGI.handleKeyboardNavigation) {
      document.removeEventListener('keydown', window.SGI.handleKeyboardNavigation);
      document.addEventListener('keydown', window.SGI.handleKeyboardNavigation);
    }
  };

  window.SGI.handleKeyboardNavigation = (e: KeyboardEvent) => {
    if (e.key === 'Tab' && currentlyOpenMenuSection) {
      // Grabs buttons and links from the currently open submenu
      // Different scopes for the root menu and submenus
      let selector = "";
      if (currentlyOpenMenuSection == menuList) {
        selector = ':scope > * > button, :scope > * > * > button, :scope > * > a, :scope > * > * > a, :scope a.header-utilities__link';
      } else {
        selector = ':scope > * > button, :scope > * > * > button, :scope > * > * > * > button, :scope > * > a, :scope > * > * > a, :scope > * > * > * > a';
      }

      e.preventDefault();
      focusTrap(e, [...currentlyOpenMenuSection.querySelectorAll(".guideline__back-link-wrapper a"), ...document.querySelectorAll('.menu__toggle-button'), ...currentlyOpenMenuSection.querySelectorAll(selector)]);
    } else if (e.key === 'Escape') {
      if (window.SGI && window.SGI.handleKeyboardNavigation) {
        document.removeEventListener('keydown', window.SGI.handleKeyboardNavigation);
      }
      toggleMobileMenu();
      setTimeout(() => {
        mobileToggleButton.focus();
      }, 1);
    }
  };

  const stopEvent = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return {
    mobileToggleButton,
    toggleMobileMenu,
    openSubMenuMobile,
    closeSubMenuMobile,
    subMenuOpenClass
  };
};